import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { MapPin, Calendar, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { id: 'tutti', label: 'Tutti i viaggi' },
  { id: 'europa', label: 'Europa' },
  { id: 'america', label: 'America' },
  { id: 'asia', label: 'Asia' },
  { id: 'africa', label: 'Africa' },
];

const CATEGORY_ACCENT = {
  europa: '#4A7C59',
  america: '#C2713A',
  asia: '#B5455A',
  africa: '#C4972A',
};

const TravelCard = ({ travel, index, featured }) => (
  <motion.div
    layout
    initial={{ opacity: 0, y: 30 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.95 }}
    transition={{ duration: 0.45, delay: index * 0.07 }}
    className={featured ? 'md:col-span-2 lg:col-span-2' : ''}
    data-testid={`travel-card-${travel.slug}`}
  >
    <Link
      to={`/viaggi/${travel.slug}`}
      className="group relative block overflow-hidden rounded-2xl"
      style={{ minHeight: featured ? '420px' : '380px' }}
    >
      <div className={`absolute inset-0 overflow-hidden`}>
        <img
          src={travel.cover_image}
          alt={travel.titolo}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
      </div>

      <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-transparent" />

      {travel.categoria && (
        <div
          className="absolute top-4 left-4 text-xs font-medium px-3 py-1.5 rounded-full text-white capitalize tracking-wide"
          style={{ backgroundColor: CATEGORY_ACCENT[travel.categoria] || '#A67C52' }}
        >
          {travel.categoria}
        </div>
      )}

      <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
        <div className="flex items-center gap-3 text-white/60 text-xs mb-3">
          <span className="flex items-center gap-1">
            <MapPin size={11} />
            {travel.destinazione}
          </span>
          <span>·</span>
          <span className="flex items-center gap-1">
            <Calendar size={11} />
            {travel.data_viaggio}
          </span>
        </div>

        <h2 className={`font-serif text-white leading-tight mb-2 ${featured ? 'text-3xl md:text-4xl' : 'text-2xl'}`}>
          {travel.titolo}
        </h2>

        <p className="text-white/75 text-sm leading-relaxed line-clamp-2 mb-4 max-w-xl">
          {travel.payoff}
        </p>

        <motion.span
          className="inline-flex items-center gap-2 text-white text-sm font-medium"
          whileHover={{ x: 4 }}
          transition={{ duration: 0.2 }}
        >
          Leggi il racconto <ArrowRight size={15} />
        </motion.span>
      </div>
    </Link>
  </motion.div>
);

const Viaggi = () => {
  const [travels, setTravels] = useState([]);
  const [activeCategory, setActiveCategory] = useState('tutti');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/travels`)
      .then(r => setTravels(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = activeCategory === 'tutti'
    ? travels
    : travels.filter(t => t.categoria === activeCategory);

  const getCount = (id) => id === 'tutti'
    ? travels.length
    : travels.filter(t => t.categoria === id).length;

  return (
    <div data-testid="viaggi-page">
      <Helmet>
        <title>Viaggi con Bambini | Destinazioni Testate in Famiglia — Chichi in Viaggio</title>
        <meta name="description" content="Scopri i nostri viaggi in famiglia con bambini piccoli: Europa, Asia, America, Africa. Itinerari testati, consigli pratici e racconti autentici." />
        <meta property="og:title" content="Viaggi con Bambini | Destinazioni Testate — Chichi in Viaggio" />
        <meta property="og:description" content="Scopri i nostri viaggi in famiglia per continente. Esperienze reali con bambini." />
      </Helmet>

      <section className="pt-32 pb-10 px-6 md:px-12 lg:px-24" data-testid="viaggi-hero">
        <div className="max-w-7xl mx-auto">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-heading font-light text-5xl md:text-7xl tracking-tight leading-none"
            data-testid="viaggi-title"
          >
            I nostri viaggi
          </motion.h1>
        </div>
      </section>

      <div className="sticky top-20 z-40 bg-white/95 backdrop-blur-md border-b border-border" data-testid="category-filters">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center gap-0 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {CATEGORIES.map((cat) => {
              const count = getCount(cat.id);
              const isActive = activeCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`relative flex items-center gap-2 px-5 py-4 text-sm whitespace-nowrap transition-all duration-200 font-medium ${
                    isActive ? 'text-primary' : 'text-foreground/50 hover:text-foreground/80'
                  }`}
                  data-testid={`category-filter-${cat.id}`}
                >
                  {cat.label}
                  <span
                    className={`text-xs px-1.5 py-0.5 rounded-full transition-all duration-200 ${
                      isActive ? 'bg-primary/15 text-primary' : 'bg-muted text-foreground/40'
                    }`}
                  >
                    {count}
                  </span>
                  {isActive && (
                    <motion.div
                      layoutId="activeCategoryLine"
                      className="absolute bottom-0 left-0 right-0 h-[2px] bg-primary rounded-full"
                      transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <section className="py-12 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-32" data-testid="loading-message">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-foreground/50 text-sm">Caricamento viaggi...</p>
            </div>
          ) : filtered.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center py-32"
              data-testid="no-travels-message"
            >
              <p className="font-serif text-3xl text-foreground/30 mb-3">
                Nessun viaggio in {activeCategory}
              </p>
              <p className="text-foreground/40 text-sm mb-6">
                Abbiamo avventure in arrivo in questa parte del mondo!
              </p>
              <button
                onClick={() => setActiveCategory('tutti')}
                className="text-primary text-sm underline hover:text-primary/70 transition-colors"
              >
                Vedi tutti i viaggi
              </button>
            </motion.div>
          ) : (
            <motion.div
              layout
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
              data-testid="travels-grid"
            >
              <AnimatePresence mode="popLayout">
                {filtered.map((travel, index) => (
                  <TravelCard
                    key={travel.slug}
                    travel={travel}
                    index={index}
                    featured={index === 0 && activeCategory === 'tutti' && filtered.length >= 3}
                  />
                ))}
              </AnimatePresence>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Viaggi;
